import { cn } from '@/utils/cn';
import { useMemo, type HTMLAttributes } from 'react';
import { MaskedScrollArea } from './masked-scrollarea';
import { ExclamationmarkTriangleFillSFSymbolMedium } from '@/assets/sf-symbols/medium/exclamationmark.triangle.fill';
import { isCreditsErrorPayload, type ErrorPayload } from './error-card-utils';

export const ErrorCard = ({
  error,
  className,
  actions,
  onRetry,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  error: ErrorPayload;
  onRetry?: () => void;
  actions?: React.ReactNode;
}) => {
  const isCreditsError = useMemo(() => isCreditsErrorPayload(error), [error]);

  if (isCreditsError) {
    return <NoCreditsErrorCard onRetry={onRetry} />;
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-3 rounded-xl',
        'border border-border',
        'bg-error-bg',
        className
      )}
      {...props}
    >
      <MaskedScrollArea
        className="text-error-fg font-medium break-all"
        scrollViewClassName="max-h-[4.375rem]"
      >
        {error.desc}
      </MaskedScrollArea>
      <div className="flex gap-2 hide-if-empty">
        {onRetry && (
          <button
            className={cn(
              'border border-border',
              'user-bubble-btn px-4 py-[0.1875rem] rounded-lg text-text-primary',
              'text-sm font-medium'
            )}
            onClick={onRetry}
          >
            Try Again
          </button>
        )}
        {actions}
      </div>
    </div>
  );
};

export const NoCreditsErrorCard = ({ onRetry }: { onRetry?: () => void }) => {
  return (
    <div className={cn('glass-card p-3 rounded-xl')}>
      <div className="flex-center size-7">
        <ExclamationmarkTriangleFillSFSymbolMedium />
      </div>
      <p className="text-[0.9375rem] font-medium tracking-normal leading-[1.375rem] mt-0.5">
        Bridge couldn&apos;t verify your credits for this message
      </p>
      <p className="text-xs opacity-90 leading-4">
        Bridge received a billing error (402). If you recently redeemed a code
        or switched devices, open Plans or sign in again, then retry after your
        account syncs.
      </p>

      <div className="mt-2 flex gap-2">
        <button
          className={cn(
            'bg-control-bg hover:bg-control-bg-hover active:bg-control-bg-active',
            'cursor-pointer transition-colors',
            'border border-border',
            'rounded-lg px-4 py-[0.1875rem]',
            'text-[0.8125rem] font-medium tracking-normal leading-[1.1875rem] text-text-primary'
          )}
          onClick={() => {
            window.jsb.UtilsBridge?.openPaywall();
          }}
        >
          Open Plans
        </button>
        {onRetry ? (
          <button
            className={cn(
              'bg-control-bg hover:bg-control-bg-hover active:bg-control-bg-active',
              'cursor-pointer transition-colors',
              'border border-border',
              'rounded-lg px-4 py-[0.1875rem]',
              'text-[0.8125rem] font-medium tracking-normal leading-[1.1875rem] text-text-primary'
            )}
            onClick={onRetry}
          >
            Try Again
          </button>
        ) : null}
      </div>
    </div>
  );
};
